import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { keys } from 'lodash';
import { isEmpty } from 'lodash-es';
import { useState, useCallback, useEffect, FC } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import {
  getAssignedJobs,
  IJobSearchFilterDTO,
  IJob,
  getWorkAssignmentByCurrentRecruiterAndJob,
  acceptWorkAssignment,
} from 'wisestep-api';
import {
  WsColors,
  Item,
  WsJobSelect,
  ControlledTextInput,
  IWorkAssignment,
  IUserDTO,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useUserContext } from '../../../context';
import { cacheJob } from '../../../utils/cacheService';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useJobScreenContext } from '../context/JobScreenContext';
import conversationWidgetStore from '../store/conversationWidgetStore';
import JobAssignmentStatusFilter from '../types/JobAssignmentStatusFilter';

import JobAssignmentSelection from './JobAssignmentSelection';

const ButtonWithIcon = styled(IconButton)(() => ({
  borderRadius: '4px',
  border: '1px solid' + WsColors.TertiaryShades300,
  padding: '10px',
  width: '6%',
}));

interface IJobSelection {
  getJob?: (args: IJob) => void;
  job?: IJob | null;
  onBackButton: () => void;
  showMapSubmission?: () => void;
}

interface IJobSelectionState {
  isStateInitialized: boolean;
  tempSelectedJob: IJob | null;
  openJobAssignmentSelection: boolean;
  assignmentData: IWorkAssignment | null;
  assignmentToTeams: string[];
  assignmentByRecruiters: string[];
  assignedJobs: IJob[];
  loader: boolean;
}
const JobSelection: FC<IJobSelection> = ({ job, getJob, onBackButton, showMapSubmission }) => {
  const [state, setState] = useState<IJobSelectionState>({
    isStateInitialized: false,
    tempSelectedJob: null,
    openJobAssignmentSelection: false,
    assignmentData: null,
    assignmentToTeams: [],
    assignmentByRecruiters: [],
    assignedJobs: [],
    loader: false,
  });
  const jobScreen = useJobScreenContext();
  const updateSelectedJob = conversationWidgetStore((state) => state.updateSelectedJob);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const methods = useForm();
  const { handleSubmit } = methods;
  const getJobs = useCallback(
    async (query: string) => {
      setState((j) => ({ ...j, loader: true }));
      const jobQuery: IJobSearchFilterDTO = {
        query: query,
        jobAssignmentStatusFilter: JobAssignmentStatusFilter.Assigned_Accepted,
        page: 0,
        size: 5,
        sort: ['postedDate,desc'],
        jobStatus: '',
        workspaceId: '',
        recruiterPartnerEmail: loggedInUser.email,
        searchAcross: [],
      };
      try {
        const jobs = await getAssignedJobs(jobQuery);
        setState((j) => ({ ...j, loader: false, assignedJobs: jobs.data }));
      } catch (e) {
        console.log(e);
      }
    },
    [loggedInUser.email]
  );

  const onFormSubmit = (data: FieldValues) => {
    getJobs(data.jobQuery);
    showMapSubmission && showMapSubmission();
  };

  const acceptJobAssignment = useCallback(
    async (assignment: IWorkAssignment, teamIdOrRecruiterId: string | undefined) => {
      try {
        if (teamIdOrRecruiterId) {
          await acceptWorkAssignment(assignment.id, teamIdOrRecruiterId);
          assignment.isAccepted = true;
          setNotification(success('Assignment accepted successfully. You can start working now.'));
          job && getJob && getJob(job);
          state.tempSelectedJob && getJob && getJob(state.tempSelectedJob);
          setState((j) => ({ ...j, tempSelectedJob: null }));
          jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: false });
          return true;
        }
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    [getJob, job, jobScreen, setNotification, state.tempSelectedJob]
  );

  const validAssignment = useCallback(
    async (job: IJob) => {
      if (job.id) {
        const api = await getWorkAssignmentByCurrentRecruiterAndJob(job.id);
        const assignment: IWorkAssignment = api.data;
        if (!isEmpty(api.data)) {
          setState((j) => ({ ...j, assignmentData: assignment }));
          if (!assignment.isClosed) {
            if (!assignment.isAccepted) {
              const assignmentToTeams = keys(assignment.assignedToTeams);
              const assignmentByRecruiters = keys(assignment.assignedByRecruiters);
              setState((j) => ({
                ...j,
                assignmentToTeams: assignmentToTeams,
                assignmentByRecruiters: assignmentByRecruiters,
              }));

              const totalAssignees = assignmentToTeams.length + assignmentByRecruiters.length;
              if (totalAssignees > 1) {
                setState((j) => ({ ...j, tempSelectedJob: job, openJobAssignmentSelection: true }));
              } else if (totalAssignees == 1) {
                return assignment?.team?.id
                  ? acceptJobAssignment(assignment, assignment.team?.id)
                  : acceptJobAssignment(assignment, assignmentByRecruiters[0]);
              } else {
                const teamIdOrRecruiterId = !isEmpty(assignmentToTeams)
                  ? assignmentToTeams[0]
                  : !isEmpty(assignmentByRecruiters)
                  ? assignmentByRecruiters[0]
                  : loggedInUser.email;
                return acceptJobAssignment(assignment, teamIdOrRecruiterId);
              }
            } else {
              getJob && getJob(job);
              return true;
            }
          } else {
            const jobOwner = job.team ? job.team.name : 'Job Owner';
            setNotification(
              warning(
                `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
              )
            );
            onBackButton && onBackButton();
            return false;
          }
        } else {
          const jobOwner = job.team ? job.team.name : 'Job Owner';
          setNotification(
            warning(
              `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
            )
          );
          onBackButton && onBackButton();
          return false;
        }
      }
    },
    [acceptJobAssignment, getJob, onBackButton, setNotification]
  );

  useEffect(() => {
    if (!state.isStateInitialized) {
      if (job != null) {
        validAssignment(job);
        jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: false });
      } else isEmpty(state.assignedJobs) && getJobs('');
      setState((j) => ({ ...j, isStateInitialized: true }));
    }
  }, [getJobs, job, jobScreen, state.assignedJobs, state.isStateInitialized, validAssignment]);

  const getSelectedJob = async (job: IJob) => {
    const valid = await validAssignment(job);
    if (valid) {
      cacheJob(job);
      getJob && getJob(job);
      updateSelectedJob(job);
      jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: false });
      showMapSubmission && showMapSubmission();
    }
  };

  const onClose = () => {
    onBackButton && onBackButton();
    jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: false });
  };

  return (
    <FormProvider {...methods}>
      {state.assignmentData !== null && state.openJobAssignmentSelection ? (
        <>
          <JobAssignmentSelection
            open={state.openJobAssignmentSelection}
            assignment={state.assignmentData}
            assignmentToTeams={state.assignmentToTeams}
            assignmentByRecruiters={state.assignmentByRecruiters}
            acceptJobAssignment={acceptJobAssignment}
            onClose={() => setState((j) => ({ ...j, openJobAssignmentSelection: true }))}
            closeJobSelectionDrawer={onClose}
          />
        </>
      ) : (
        <>
          {isEmpty(job) ? (
            <>
              <Grid
                container
                item
                px={2.5}
                pt={2}
                component="form"
                onSubmit={handleSubmit(onFormSubmit)}
              >
                <ButtonWithIcon onClick={onClose} sx={{ cursor: 'pointer' }}>
                  <KeyboardBackspaceIcon onClick={onClose} sx={{ cursor: 'pointer' }} />
                </ButtonWithIcon>
                <ControlledTextInput
                  name="jobQuery"
                  type="text"
                  sx={{ width: '88%', px: 0.5 }}
                  placeholder="Search for a job"
                />
                <ButtonWithIcon type="submit">
                  <SearchIcon style={{ color: WsColors.GreyBlack }} />
                </ButtonWithIcon>
              </Grid>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sx={{
                    height: 900,
                    overflowY: 'auto',
                  }}
                >
                  {isEmpty(state.assignedJobs) && state.loader ? (
                    <Box textAlign="center" pt="30%">
                      <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
                    </Box>
                  ) : isEmpty(state.assignedJobs) ? (
                    <NoDataFoundCard text="No Jobs Found" />
                  ) : (
                    <Item>
                      {state.assignedJobs.map((job: IJob, index: number) => (
                        <WsJobSelect key={index} job={job} getSelectedJob={getSelectedJob} />
                      ))}
                    </Item>
                  )}
                </Grid>
              </Grid>
            </>
          ) : (
            <Box textAlign="center" pt="25%">
              <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
            </Box>
          )}
        </>
      )}
    </FormProvider>
  );
};

export default JobSelection;
